import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import DialogActions from "@material-ui/core/DialogActions";
import MuiDialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import Button from "@material-ui/core/Button";

import { getMercariUser } from "src/utils/extension/mercari";
import type { Cookie } from "src/utils/extension";
import {
  getMercariIntegration,
  mercariIntegrationCreate,
} from "src/apiService/modules/mercari";
import { AddNewSalePlatformIntegrationButton } from "src/components/AddNewSalePlatformIntegrationDialog";

import { StatefulLoadableButton } from "src/components/LoadableButton";
import DialogTitle from "./DialogTitle";
import type Step from "./Step";
import {
  completeUserSetupQuestionCardPlatform,
  doesUserHaveCardPlatform,
} from "src/apiService/modules/users";
import { useDispatch } from "react-redux";
import { mercariSyncSales } from "src/store/integrationSync/actions/mercari";
import MonthYear from "src/interfaces/monthYear.interface";
import { useRecordEvent } from "src/hooks/useRecordEvent";

interface MercariConfirmProps {
  onClose: () => void;
  onStepChange: (step: Step) => void;
  showConnectSalePlatform?: boolean;
}

function MercariConfirm({
  onClose,
  onStepChange,
  showConnectSalePlatform = true,
}: MercariConfirmProps) {
  const dispatch = useDispatch();
  const recordEvent = useRecordEvent();
  const [mercariUserData, setMercariUserData] = useState<
    | {
        username: string;
        cookies: Cookie[];
      }
    | { error: string }
    | { loading: true }
  >({ loading: true });

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const data = await getMercariUser();
        if (cancel) return;
        setMercariUserData(data);
      } catch (e) {
        if (cancel) return;
        setMercariUserData({ error: (e as Error).message });
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const onConnect = async (bankAccountId?: string) => {
    if ("cookies" in mercariUserData && mercariUserData.cookies) {
      const existingMercariIntegration = await getMercariIntegration();
      if (existingMercariIntegration?.id) {
        if (await doesUserHaveCardPlatform("Mercari")) {
          await completeUserSetupQuestionCardPlatform("Mercari");
          const currentDate = new Date();
          dispatch(
            mercariSyncSales(existingMercariIntegration.id, {
              start: {
                month: 1,
                year: currentDate.getFullYear() - 1,
              },
              end: {
                month: currentDate.getMonth() + 1,
                year: currentDate.getFullYear(),
              } as MonthYear,
            })
          );
        }
        toast.info("That Mercari account is already connected.");
        onStepChange("mercari:import");
        onClose();
        return;
      }

      await mercariIntegrationCreate({
        cookies: mercariUserData.cookies,
        username: mercariUserData?.username,
        lastSync: new Date().toISOString(),
        bankAccountId,
      });
      recordEvent({
        type: "feature-adoption",
        feature: "integration-added",
        element: "sale-platform-mercari",
      });
      onStepChange("mercari:import");
      if (await doesUserHaveCardPlatform("Mercari")) {
        const mercariIntegration = await getMercariIntegration();
        await completeUserSetupQuestionCardPlatform("Mercari");
        if (mercariIntegration?.id) {
          const currentDate = new Date();
          dispatch(
            mercariSyncSales(mercariIntegration.id, {
              start: {
                month: 1,
                year: currentDate.getFullYear() - 1,
              },
              end: {
                month: currentDate.getMonth() + 1,
                year: currentDate.getFullYear(),
              } as MonthYear,
            })
          );
        }
      }
      onClose();
    }
  };

  return (
    <>
      <DialogTitle onClose={onClose}>Add Mercari integration</DialogTitle>
      <MuiDialogContent>
        <DialogContentText>
          {"username" in mercariUserData
            ? `The Mercari account "${mercariUserData.username}" will be added.`
            : "error" in mercariUserData
            ? `ERROR: ${mercariUserData.error}`
            : "Loading..."}
        </DialogContentText>
      </MuiDialogContent>
      <DialogActions>
        <Button onClick={() => onStepChange("mercari:do-you-sell-on-mercari")}>
          Cancel
        </Button>
        {showConnectSalePlatform ? (
          <AddNewSalePlatformIntegrationButton
            variant="contained"
            disabled={
              !!(mercariUserData as any).error ||
              (mercariUserData as any).loading
            }
            onConnect={onConnect}
            color="primary"
            autoFocus
          >
            Add
          </AddNewSalePlatformIntegrationButton>
        ) : (
          <StatefulLoadableButton
            color="primary"
            variant="contained"
            disabled={
              !!(mercariUserData as any).error ||
              (mercariUserData as any).loading
            }
            onClick={async () => await onConnect()}
          >
            Connect
          </StatefulLoadableButton>
        )}
      </DialogActions>
    </>
  );
}

export default MercariConfirm;
